import { ajax } from "$lib/ajax";
import { API_BASE_URL } from "$lib/config/index";
import { setMode } from "mode-watcher"; // Import setMode to set the theme
import { get, writable, type Writable } from "svelte/store";

import { emailTemplates, type Template } from "./stores/emailTemplateStore";
import { navbarStore } from "./stores/navbarStore";

type LoginStatus = "yes" | "no" | "needs-mfa";

export type Organization = {
    id: number;
    email: string;
};

export type PmsConnection = {
    id: number;
    username: string;
};

export type Modal = {
    id: number;
    title: string;
    message: string;
    url_to_trigger: string | null;
    delay_in_seconds: number;
};

export type OrganizationUser = {
    id: number;
    first_name: string;
    last_name: string;
};

export class User {
    loginStatus: LoginStatus;
    email: string | null;
    name: string | null;
    first_name: string | null;
    last_name: string | null;
    hasMfa: boolean;
    isOrganizationAdmin: boolean;
    auto_add_patient_email: boolean;
    speciality: string;
    unverifiedDictations: number;
    verifiedDictations: number;
    reports: number;
    has_payment_method: boolean;
    addedPMS: boolean;
    organizations: Organization[];
    organizationId: number;
    pmsConnections: PmsConnection[];
    hasPlan: boolean;
    is_admin: boolean;
    modals: Modal[];
    users: OrganizationUser[];
    auto_add_doctors: boolean;
    overdueCount: number;
    doneCount: number;
    totalDictationsCount: number;
    autocorrect_enabled: boolean;
    constructor(
        loginStatus: LoginStatus,
        email: string | null,
        name: string | null,
        first_name: string | null,
        last_name: string | null,
        hasMfa: boolean,
        isOrganizationAdmin: boolean,
        auto_add_patient_email: boolean,
        speciality: string,
        unverifiedDictations: number,
        verifiedDictations: number,
        reports: number,
        has_payment_method: boolean,
        addedPMS: boolean,
        organizations: Organization[],
        organization_id: number,
        pms_connections: PmsConnection[],
        has_plan: boolean,
        is_admin: boolean,
        modals: Modal[],
        users: OrganizationUser[],
        auto_add_doctors: boolean,
        overdueCount: number,
        doneCount: number,
        totalDictationsCount: number,
        autocorrect_enabled: boolean
    ) {
        this.loginStatus = loginStatus;
        this.email = email;
        this.name = name;
        this.first_name = first_name;
        this.last_name = last_name;
        this.hasMfa = hasMfa;
        this.isOrganizationAdmin = isOrganizationAdmin;
        this.auto_add_patient_email = auto_add_patient_email;
        this.speciality = speciality;
        this.unverifiedDictations = unverifiedDictations;
        this.verifiedDictations = verifiedDictations;
        this.reports = reports;
        this.has_payment_method = has_payment_method;
        this.addedPMS = addedPMS;
        this.organizations = organizations;
        this.organizationId = organization_id;
        this.pmsConnections = pms_connections;
        this.hasPlan = has_plan;
        this.is_admin = is_admin;
        this.modals = modals;
        this.users = users;
        this.auto_add_doctors = auto_add_doctors;
        this.overdueCount = overdueCount;
        this.doneCount = doneCount;
        this.totalDictationsCount = totalDictationsCount;
        this.autocorrect_enabled = autocorrect_enabled;
    }

    static readonly #empty = new User(
        "no",
        null,
        null,
        null,
        null,
        false,
        false,
        true,
        "",
        0,
        0,
        0,
        true,
        true,
        [],
        0,
        [],
        true,
        false,
        [],
        [],
        false,
        0,
        0,
        0,
        true
    );

    static readonly #user: Writable<User> = writable(User.#empty);
    static #loading: Promise<User> | null = null;

    static async load(): Promise<User> {
        // If there's already a load in progress, return that promise
        if (User.#loading) {
            return User.#loading;
        }

        // Create a new loading promise
        User.#loading = (async () => {
            try {
                const data = await ajax<{
                    login: LoginStatus;
                    email: string | null;
                    name: string | null;
                    first_name: string | null;
                    last_name: string | null;
                    has_mfa: boolean;
                    is_organization_admin: boolean;
                    auto_add_patient_email: boolean;
                    speciality: string;
                    unverified_dictations: number;
                    verified_dictations: number;
                    reports: number;
                    has_payment_method: boolean;
                    added_pms: boolean;
                    organizations: Organization[];
                    organization_id: number;
                    pms_connections: PmsConnection[];
                    has_plan: boolean;
                    is_admin: boolean;
                    modals: Modal[];
                    users: OrganizationUser[];
                    email_templates: Template[];
                    auto_add_doctors: boolean;
                    overdueCount: number;
                    doneCount: number;
                    total_dictations: number;
                    autocorrect_enabled: boolean;
                }>(`${API_BASE_URL}/accounts/me/`);

                if (data instanceof Blob || data === null) {
                    throw new Error("Failed to fetch user data");
                }

                const user = new User(
                    data.login,
                    data.email ?? null,
                    data.name ?? null,
                    data.first_name ?? null,
                    data.last_name ?? null,
                    data.has_mfa ?? false,
                    data.is_organization_admin ?? false,
                    data.auto_add_patient_email ?? false,
                    data.speciality ?? "",
                    data.unverified_dictations ?? 0,
                    data.verified_dictations ?? 0,
                    data.reports ?? 0,
                    data.has_payment_method ?? false,
                    data.added_pms ?? false,
                    data.organizations ?? [],
                    data.organization_id ?? 0,
                    data.pms_connections ?? [],
                    data.has_plan ?? false,
                    data.is_admin ?? false,
                    data.modals ?? [],
                    data.users ?? [],
                    data.auto_add_doctors ?? false,
                    data.overdueCount ?? 0,
                    data.doneCount ?? 0,
                    data.total_dictations ?? 0,
                    data.autocorrect_enabled ?? false
                );

                User.#user.set(user);

                // Fetch and set the theme preference
                if (user.loginStatus === "yes") {
                    try {
                        // set email templates
                        emailTemplates.set(data.email_templates);

                        navbarStore.set({
                            todoCount: user.unverifiedDictations,
                            verifiedCount: user.verifiedDictations,
                            doneCount: user.doneCount,
                            overdueCount: user.overdueCount,
                            totalDictationsCount: user.totalDictationsCount,
                        });

                        const themeResponse = await ajax<{ success: boolean; theme: string }>(
                            `${API_BASE_URL}/accounts/theme_preference/get/`,
                            {},
                            "json",
                            "GET"
                        );
                        if (themeResponse.success) {
                            setMode(themeResponse.theme);
                        }
                    } catch (error) {
                        console.error("Error fetching theme preference:", error);
                    }
                }

                return user;
            } finally {
                // Clear the loading promise once done
                User.#loading = null;
            }
        })();

        return User.#loading;
    }

    static subscribe(callback: (value: User) => void) {
        return User.#user.subscribe(callback);
    }

    static async login(email: string, password: string): Promise<boolean> {
        const { success } = await ajax<{ success: boolean }>(`${API_BASE_URL}/accounts/login/`, {
            email,
            password,
        });
        console.log(success);
        if (success) {
            await User.load();
        } else {
            User.#user.set(User.#empty);
        }
        return success;
    }

    static async register(
        firstName: string,
        lastName: string,
        email: string,
        password: string
    ): Promise<{ success: boolean }> {
        const result = await ajax<{ success: boolean }>(`${API_BASE_URL}/accounts/register/`, {
            first_name: firstName,
            last_name: lastName,
            email: email,
            password: password,
        });
        if (result.success) {
            await User.load();
        } else {
            User.#user.set(User.#empty);
        }
        return result;
    }

    static async logout(): Promise<void> {
        await ajax(`${API_BASE_URL}/accounts/logout/`);
        User.#user.set(User.#empty);
    }

    static update(user: User) {
        User.#user.set(user);
    }

    static set(user: User) {
        User.#user.set(user);
    }
}
